import axios from 'axios';
import queryString from 'query-string';
import { signOutAction } from 'domain/env/envActions';
import Cookies from 'js-cookie';

const methods = {
  post: 'post',
  get: 'get',
  put: 'put',
  delete: 'delete',
  patch: 'patch',
};

export const CONFIGURABLE_API_ENDPOINT =
  process.env.REACT_APP_IGNORE_API_ENDPOINT_COOKIE !== '1'
    ? Cookies.get(process.env.REACT_APP_API_ENDPOINT_COOKIE_NAME) || process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL;

export const CANARY_COOKIE_NAME = process.env.REACT_APP_CANARY_COOKIE_NAME || 'deliveryMethod';

export const CONFIGURABLE_PAY_API_ENDPOINT = process.env.REACT_APP_PAY_API_URL;

const triesKey = 'dokka-app-reload-tries';

export function makeUrl(strings, ...values) {
  return (args) => values.reduceRight((a, v, i) => `${strings[i]}${args[v]}${a}`, strings[strings.length - 1]);
}

export const getResponseInterceptors = (store) => {
  const successHandler = (response) => response;

  const errorHandler = (error) => {
    // requests failing in worker dont have access to window
    if (typeof window !== 'undefined') {
      if (error.response && error.response.status === 426) {
        const triesFromLS = window.localStorage.getItem(triesKey) || 0;
        if (triesFromLS < 4) {
          window.localStorage.setItem(triesKey, parseFloat(triesFromLS) + 1);
          window.location.reload(true);
        } else {
          window.localStorage.removeItem(triesKey);
          store.dispatch({ type: signOutAction.type });
        }
      }

      const { REACT_APP_ACCESS_DENIED_CODE } = process.env;
      if (error.response && error.response.status.toString() === REACT_APP_ACCESS_DENIED_CODE) {
        const accessDeniedReasonType = error.response.data?.type;
        store.dispatch({
          type: signOutAction.type,
          payload: {
            reason: REACT_APP_ACCESS_DENIED_CODE,
            ...(accessDeniedReasonType && { accessDeniedReasonType }),
          },
        });
      }
    }
    return Promise.reject(error);
  };

  return [successHandler, errorHandler];
};

function urlHelper(baseUrl) {
  return (uri) => {
    if (typeof uri === 'string') {
      return () => `${baseUrl}${uri}`;
    }
    if (typeof uri === 'function') {
      return (args) => `${baseUrl}${uri(args)}`;
    }
    throw new Error('Incorrect uri parametr');
  };
}

export function makeRequest(method, url, tokenKeeper = null) {
  return async (args) => {
    const params = {
      method,
      // timeout: 5000, // not enabled for now
      url: url(args),
      ...args,
      params: Object.assign(args.params || {}, {
        'x-dokka-app-version': process.env.REACT_APP_BUILD_VERSION,
        'x-dokka-app-env': process.env.REACT_APP_ENV,
      }),
      headers: {
        ...args.headers,
        ...(Cookies.get(CANARY_COOKIE_NAME) && { [CANARY_COOKIE_NAME]: Cookies.get(CANARY_COOKIE_NAME) }),
      },
    };

    const requestData = tokenKeeper ? await tokenKeeper.updateRequestData(params) : params;
    return axios(requestData);
  };
}

export function requestCreator(baseUrl, tokenKeeper = null) {
  const url = urlHelper(baseUrl);
  return Object.keys(methods).reduce(
    (a, v) => ({
      ...a,
      [v]: (uri) => makeRequest(methods[v], url(uri), tokenKeeper),
    }),
    {},
  );
}

export class MakeRequest {
  constructor(baseUrl, method, uri, validator, loger, tokenKeeper) {
    this._baseUrl = baseUrl;
    this._method = method;
    this._uri = uri;
    this._validator = validator;
    this._loger = loger;
    this._tokenKeeper = tokenKeeper;
  }

  _baseUrl;

  _uri;

  _method;

  _validator;

  _loger;

  _tokenKeeper;

  url(params) {
    if (typeof this._uri === 'string') {
      return this._baseUrl + this._uri;
    }
    return this._baseUrl + this._uri(params);
  }

  static params(params = {}) {
    return {
      'x-dokka-app-version': process.env.REACT_APP_BUILD_VERSION,
      'x-dokka-app-env': process.env.REACT_APP_ENV,
      ...params,
    };
  }

  async config({ params, ...args }) {
    const res = {
      method: this._method,
      url: this.url(args),
      params: this.constructor.params(params),
      ...args,
      headers: {
        ...args.headers,
        ...(Cookies.get(CANARY_COOKIE_NAME) && { [CANARY_COOKIE_NAME]: Cookies.get(CANARY_COOKIE_NAME) }),
      },
    };

    return this._tokenKeeper.updateRequestData(res);
  }

  async request(params, responseModifier) {
    return new Promise(async (resolve, reject) => {
      const config = await this.config(params || {});
      axios(config)
        .then((resp) => {
          try {
            this._validator(resp.data);
          } catch (err) {
            this._loger(err, resp);
            // TODO: add tag to logger if validation error
          }
          const handler = typeof responseModifier === 'function' ? responseModifier : (x) => x;
          return resolve(handler(resp));
        })
        .catch((err) => {
          console.log('ERROR', err);
          reject(err);
        });
    });
  }
}

export class MakePaymentRequest extends MakeRequest {
  async config({ params, ...args }) {
    const res = {
      method: this._method,
      url: this.url(args),
      params: this.constructor.params(params),
      ...args,
    };
    return this._tokenKeeper.updatePaymentRequestData(res);
  }
}

export class RequestCreator {
  constructor(baseUrl, loger, tokenKeeper) {
    this._baseUrl = baseUrl;
    this._loger = loger;
    this._tokenKeeper = tokenKeeper;
  }

  _baseUrl;

  _loger;

  _tokenKeeper;

  _request(method, uri, validator, responseModifier = null) {
    const instance = new MakeRequest(this._baseUrl, method, uri, validator, this._loger, this._tokenKeeper);
    return (p) => instance.request(p, responseModifier);
  }

  get(uri, validator, responseModifier) {
    return this._request('get', uri, validator, responseModifier);
  }

  post(uri, validator) {
    return this._request('post', uri, validator);
  }

  put(uri, validator) {
    return this._request('put', uri, validator);
  }

  patch(uri, validator) {
    return this._request('patch', uri, validator);
  }

  delete(uri, validator) {
    return this._request('delete', uri, validator);
  }

  url(uri) {
    return (query) => `${this._baseUrl + uri}?${queryString.stringify(query, { arrayFormat: 'bracket' })}`;
  }

  mock(response, validator) {
    return (...args) =>
      Promise.resolve(typeof response === 'function' ? response(...args) : response).then((resp) => {
        try {
          validator(resp);
        } catch (err) {
          // $FlowFixMe
          this._loger(err, resp);
        }
        // make compatible with axios requests that wrap response with 'data' key
        return { data: resp };
      });
  }
}

export class PaymentRequestCreator extends RequestCreator {
  _request(method, uri, validator, responseModifier = null) {
    const instance = new MakePaymentRequest(this._baseUrl, method, uri, validator, this._loger, this._tokenKeeper);
    return (p) => instance.request(p, responseModifier);
  }

  url(uri) {
    return (query) => {
      const username = this._tokenKeeper.getUsername();
      const params = username ? { ...query, username } : query;
      return `${this._baseUrl + uri}?${queryString.stringify(params, { arrayFormat: 'bracket' })}`;
    };
  }
}
